import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // WHY: keep dev visibility while leaving the door open for a real
    // observability sink (Sentry/App Insights) wired up later.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  override render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="page">
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-rose-700">Something went wrong</p>
            <h2 className="h-display mt-2 text-2xl">{this.state.error.message}</h2>
            <p className="mt-2 text-sm text-rose-700/80">
              The page hit an unexpected error. Try again, or refresh if the problem persists.
            </p>
            <div className="mt-4">
              <Button variant="primary" onClick={this.reset}>
                Try again
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
